import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { OwnerLayout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { Progress } from '../../components/ui/progress';
import { ArrowLeft, Lock, CheckCircle, Play, Clock } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OwnerSeriesDetail() {
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const { activeDog } = useAuth();
  const [series, setSeries] = useState(null);
  const [videos, setVideos] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [seriesId, activeDog]);

  const fetchData = async () => {
    if (!activeDog) {
      setLoading(false);
      return;
    }

    try {
      const [seriesRes, videosRes, progressRes] = await Promise.all([
        axios.get(`${API}/series/${seriesId}`),
        axios.get(`${API}/videos/series/${seriesId}`),
        axios.get(`${API}/progress/${activeDog.id}`)
      ]);

      setSeries(seriesRes.data);
      setVideos(videosRes.data);

      // Map progress by video_id
      const progressMap = {};
      progressRes.data.forEach(p => {
        progressMap[p.video_id] = p;
      });
      setProgress(progressMap);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getVideoStatus = (videoId) => {
    return progress[videoId]?.status || 'locked';
  };

  const completedCount = videos.filter(v => getVideoStatus(v.id) === 'complete').length;
  const totalCount = videos.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-bounce-subtle">
            <span className="text-5xl">🐕</span>
          </div>
        </div>
      </OwnerLayout>
    );
  }

  if (!series) {
    return (
      <OwnerLayout>
        <div className="text-center py-16">
          <h2 className="font-heading text-2xl font-bold text-gray-800 mb-4">
            Series Not Found
          </h2>
          <Link to="/owner/series" className="text-edupaws-sage font-body">
            Back to Training
          </Link>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout>
      {/* Back Button */}
      <button
        onClick={() => navigate('/owner/series')}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 font-body"
        data-testid="back-to-series"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Training
      </button>

      {/* Series Header */}
      <div className="bg-white rounded-3xl p-6 md:p-8 border-2 border-gray-100 mb-8">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Series Info */}
          <div className="flex-1">
            <span className="inline-block bg-edupaws-sage/20 text-edupaws-navy px-3 py-1 rounded-full text-sm font-heading font-bold mb-3">
              Series {series.order_index}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-800 mb-3">
              {series.title}
            </h1>
            <p className="text-gray-500 font-body text-lg mb-6">
              {series.description}
            </p>

            {/* Progress */}
            <div className="max-w-md">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500 font-body">
                  {completedCount}/{totalCount} videos completed
                </span>
                <span className="text-sm font-heading font-bold text-edupaws-sage">
                  {progressPercent}%
                </span>
              </div>
              <Progress value={progressPercent} className="h-4" />
            </div>
          </div>

          {/* Stats */}
          <div className="flex md:flex-col gap-4 md:gap-2 md:min-w-[160px]">
            <div className="flex items-center gap-3 bg-edupaws-sage/10 rounded-2xl px-4 py-3">
              <CheckCircle className="w-5 h-5 text-edupaws-sage" />
              <div>
                <div className="font-heading font-bold text-gray-800">{completedCount}</div>
                <div className="text-xs text-gray-500 font-body">Completed</div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-orange-50 rounded-2xl px-4 py-3">
              <Play className="w-5 h-5 text-orange-500" />
              <div>
                <div className="font-heading font-bold text-gray-800">
                  {videos.filter(v => getVideoStatus(v.id) === 'unlocked').length}
                </div>
                <div className="text-xs text-gray-500 font-body">Ready</div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-gray-50 rounded-2xl px-4 py-3">
              <Lock className="w-5 h-5 text-gray-400" />
              <div>
                <div className="font-heading font-bold text-gray-800">
                  {videos.filter(v => getVideoStatus(v.id) === 'locked').length}
                </div>
                <div className="text-xs text-gray-500 font-body">Locked</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Videos Grid */}
      <h2 className="font-heading text-xl font-bold text-gray-800 mb-4">
        Videos in this Series
      </h2>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {videos.map((video, idx) => {
          const status = getVideoStatus(video.id);
          const isLocked = status === 'locked';
          const isComplete = status === 'complete';
          const isUnlocked = status === 'unlocked';

          return (
            <Link
              key={video.id}
              to={isLocked ? '#' : `/owner/video/${video.id}`}
              data-testid={`video-tile-${video.order_index}`}
              onClick={(e) => isLocked && e.preventDefault()}
              className={`relative bg-white rounded-2xl p-5 border-2 transition-all duration-300
                ${isLocked 
                  ? 'border-gray-100 opacity-60 cursor-not-allowed' 
                  : isComplete
                    ? 'border-edupaws-sage/40 hover:border-edupaws-navy hover:-translate-y-1 hover:shadow-lg'
                    : 'border-gray-100 hover:border-orange-300 hover:-translate-y-1 hover:shadow-lg'
                }`}
            >
              {/* Video Number */}
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4
                ${isComplete 
                  ? 'bg-edupaws-sage/20' 
                  : isUnlocked 
                    ? 'bg-edupaws-sage/20' 
                    : 'bg-gray-100'
                }`}>
                {isComplete ? (
                  <CheckCircle className="w-6 h-6 text-edupaws-sage" />
                ) : isLocked ? (
                  <Lock className="w-6 h-6 text-gray-400" />
                ) : (
                  <span className="font-heading font-bold text-orange-500 text-xl">
                    {video.order_index}
                  </span>
                )}
              </div>

              {/* Video Info */}
              <h3 className="font-heading text-lg font-bold text-gray-800 mb-2 line-clamp-2">
                {video.title}
              </h3>
              <p className="text-gray-500 font-body text-sm mb-4 line-clamp-2">
                {video.goal_outcome}
              </p>

              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-body font-medium
                  ${isComplete 
                    ? 'bg-edupaws-sage/20 text-edupaws-navy' 
                    : isUnlocked 
                      ? 'bg-edupaws-sage/20 text-edupaws-navy'
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                  {isComplete ? (
                    <>
                      <CheckCircle className="w-3 h-3" /> Completed
                    </>
                  ) : isUnlocked ? (
                    <>
                      <Play className="w-3 h-3" /> Ready to Watch
                    </>
                  ) : !video.published ? (
                    <>
                      <Clock className="w-3 h-3" /> Coming Soon
                    </>
                  ) : (
                    <>
                      <Lock className="w-3 h-3" /> Complete previous video
                    </>
                  )}
                </span>
              </div>

              {/* Locked Overlay */}
              {isLocked && (
                <div className="absolute inset-0 rounded-2xl bg-white/50 backdrop-blur-[1px]" />
              )}
            </Link>
          );
        })}
      </div>
    </OwnerLayout>
  );
}
